import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Button } from "@trillion/ui/components/ui/button";
import { Badge } from "@trillion/ui/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin-api";
import { useProducts } from "@/hooks/use-products";
import type { UserRole } from "@/types";

const ROLES = ["admin", "user", "editor"];

interface RoleManagerProps {
  userId: string;
  roles: UserRole[];
  onRolesChanged: () => void;
}

export function RoleManager({
  userId,
  roles,
  onRolesChanged,
}: RoleManagerProps) {
  const [newProduct, setNewProduct] = useState<string>("");
  const [newRole, setNewRole] = useState<string>("");
  const [isAdding, setIsAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<UserRole | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const { products, isLoading: productsLoading } = useProducts();

  async function handleAddRole() {
    if (!newProduct || !newRole) {
      toast.error("Please select both a product and a role.");
      return;
    }

    const exists = roles.some(
      (r) => r.product === newProduct && r.role === newRole
    );
    if (exists) {
      toast.error("This role already exists for the user.");
      return;
    }

    setIsAdding(true);
    try {
      await adminApi.users.addRole(userId, newProduct, newRole);
      toast.success(`Added ${newRole} role for ${newProduct}.`);
      setNewProduct("");
      setNewRole("");
      onRolesChanged();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to add role."
      );
    } finally {
      setIsAdding(false);
    }
  }

  async function handleRemoveRole() {
    if (!removeTarget) return;

    setIsRemoving(true);
    try {
      await adminApi.users.removeRole(
        userId,
        removeTarget.product,
        removeTarget.role
      );
      toast.success(
        `Removed ${removeTarget.role} role for ${removeTarget.product}.`
      );
      setRemoveTarget(null);
      onRolesChanged();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to remove role."
      );
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {/* Existing Roles Table */}
          {roles.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={`${role.product}-${role.role}`}>
                    <TableCell>
                      <Badge variant="secondary">{role.product}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{role.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setRemoveTarget(role)}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">
              No roles assigned to this user.
            </p>
          )}

          {/* Add Role Form */}
          <div>
            <h4 className="mb-3 text-sm font-semibold">Add Role</h4>
            <div className="flex items-end gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground">Product</label>
                <Select value={newProduct} onValueChange={setNewProduct}>
                  <SelectTrigger className="w-[160px]" disabled={productsLoading}>
                    <SelectValue
                      placeholder={productsLoading ? "Loading products…" : "Select product"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.slug} value={p.slug}>
                        {p.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground">Role</label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleAddRole}
                disabled={!newProduct || !newRole || isAdding}
                size="sm"
              >
                {isAdding ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <Plus className="mr-1 size-4" />
                )}
                Add Role
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Remove Role"
        description={
          removeTarget
            ? `Are you sure you want to remove the "${removeTarget.role}" role for "${removeTarget.product}" from this user?`
            : ""
        }
        confirmLabel="Remove"
        onConfirm={handleRemoveRole}
        variant="destructive"
        isLoading={isRemoving}
      />
    </>
  );
}
